/**
 * HeaderContainer wires deps and focus navigation into the Header UI.
 *
 * Why: Containers handle dep injection and hook setup. Header is a large app
 * component that receives VM directly and reads signals itself.
 *
 * When to use: Use this container in the app shell.
 */
import * as React from 'react'
import { useSignals } from '@preact/signals-react/runtime'
import { Header } from '../Header/Header'

import { useFocusNavigationDep, useControllerInputDep } from '../../providers/FocusNavigationProvider/FocusNavigationProvider'
import { useHeaderFocusNavigation } from '../../../hooks/useHeaderFocusNavigation'
import { GameOptionsDialog } from '../../playArea/GameOptionsDialog/GameOptionsDialog'
import { useWebContainer } from '../../providers/DepProvider/DepProvider'
import type { ChallengeConfiguration } from '@merels/room'
import { ChallengeConfigurationControl } from '../Header/ChallengeConfigurationControl'

export function HeaderContainer() {
  useSignals()

  const container = useWebContainer()
  const shellVM = container.shellVM
  const gameId = shellVM.game.gameId.value
  const developmentProgress = gameId
    ? container.gameRegistry.getGameBuildInfo?.(gameId)?.value?.developmentProgress
    : undefined
  const activeGameMetadata = gameId ? container.roomState.activeGame.value?.gameVersion?.metadata : null
  const catalogMetadata = gameId ? container.gameRegistry.getGameMetadata?.(gameId) : null
  const mateCount = container.roomState.mates.value.length
  // The control restarts into the current catalog version, so it must use
  // current capabilities instead of metadata pinned by the game being replaced.
  const challengeSources = catalogMetadata?.challengeSources ?? activeGameMetadata?.challengeSources ?? []
  const gameChallengeArrangements = catalogMetadata?.gameChallengeArrangements ?? activeGameMetadata?.gameChallengeArrangements ?? []
  const availableChallengeConfigurations: ChallengeConfiguration[] = [
    ...(mateCount >= 2 && challengeSources.includes('opponent') ? [{ challengeSource: 'opponent' } as const] : []),
    ...(challengeSources.includes('game') && gameChallengeArrangements.includes('cooperative') ? [{ challengeSource: 'game', gameChallengeArrangement: 'cooperative' } as const] : []),
    ...(mateCount >= 2 && challengeSources.includes('game') && gameChallengeArrangements.includes('sameChallengeRace') ? [{ challengeSource: 'game', gameChallengeArrangement: 'sameChallengeRace' } as const] : []),
    ...(mateCount >= 2 && challengeSources.includes('game') && gameChallengeArrangements.includes('differentChallengeRace') ? [{ challengeSource: 'game', gameChallengeArrangement: 'differentChallengeRace' } as const] : []),
  ]
  // A running dev session can retain a room-state dep created before this
  // platform field existed. Hide the optional control until that dep reloads.
  const activeChallengeConfiguration = container.roomState.activeGame.value?.challengeConfiguration
  const challengeConfigurationControl = gameId && availableChallengeConfigurations.length > 1 && container.roomActions.setChallengeConfigurationPreference ? (
    <ChallengeConfigurationControl
      activeChallengeConfiguration={activeChallengeConfiguration}
      availableChallengeConfigurations={availableChallengeConfigurations}
      onSaveAndRestart={async (challengeConfiguration) => {
        await container.roomActions.setChallengeConfigurationPreference(gameId, challengeConfiguration)
        await container.roomActions.endGame()
        await container.debugPanelVM.openGame(gameId)
      }}
    />
  ) : undefined

  const focusNavigation = useFocusNavigationDep()
  const controllerInput = useControllerInputDep()

  const headerNav = useHeaderFocusNavigation({
    hasRoomMenu: true,
    hasGameControls: Boolean(gameId),
    canControlGames: shellVM.room.canControlGames.value,
    hasPlayAgain: shellVM.game.isGameOver.value,
    hasHints: shellVM.game.hasHints.value,
    hasRulebook: shellVM.game.hasRulebook.value,
    hasConfigure: shellVM.game.hasGameOptions.value,
    hasPause: Boolean(shellVM.game.hasTimers.value && !shellVM.game.isGameOver.value),
  })

  // Subscribe to semantic input events for header navigation
  React.useEffect(() => {
    const unsubscribe = controllerInput.subscribe((event) => {
      if (focusNavigation.focusArea.value === 'header' && focusNavigation.isActive.value) {
        if (event.type === 'navigate' && (event.direction === 'left' || event.direction === 'right')) {
          headerNav.handleNavigate(event.direction)
        } else if (event.type === 'select') {
          headerNav.handleSelect()
        }
      }
    })
    return unsubscribe
  }, [controllerInput, focusNavigation, headerNav])

  const isOwnedGame = container.debugPanelVM?.isOwnedGame?.value ?? false
  const onDevelopmentStatusClick = isOwnedGame
    ? () => container.debugPanelVM.openAndSelectTool('status')
    : undefined

  return (
    <>
      {gameId && shellVM.game.hasGameOptions.value ? (
        <GameOptionsDialog
          open={shellVM.configureDialog.isOpen.value}
          onOpenChange={shellVM.configureDialog.setIsOpen}
          gameId={gameId}
          savedOptions={container.roomState.savedOptions.value}
          roomActions={container.roomActions}
          gameRegistry={container.gameRegistry}
          mode="inGame"
          availablePlayerCounts={container.gameOptionsDialog.availablePlayerCounts.value}
          defaultPlayerCount={container.gameOptionsDialog.defaultPlayerCount.value}
        />
      ) : null}
      <Header
        shellVM={shellVM}
        focusedItem={headerNav.focusedItem}
        getRefCallback={headerNav.getRefCallback}
        challengeConfigurationControl={challengeConfigurationControl}
        developmentProgress={developmentProgress}
        onDevelopmentStatusClick={onDevelopmentStatusClick}
      />
    </>
  )
}
